/**
 * Onboarding Types
 *
 * Types for the organisation admin practitioner onboarding flow.
 * These types model the request/response payloads for the backend
 * `/admin/onboard-user` endpoint and the supporting `/admin/roles`
 * endpoint.
 */

/**
 * Role option for the onboarding form.
 * `level` is retained for compatibility with older role-list responses.
 */
export interface RoleOption {
  code: string;
  display: string;
  description: string;
  level: number;
}

/**
 * Request payload for POST /admin/onboard-user
 */
export interface OnboardUserRequest {
  email: string;
  password: string;
  role: string;
  firstName: string;
  lastName: string;
  /**
   * Required for practitioners. The first entry is the primary specialty and must be
   * a catalogue code; the rest may be catalogue codes, display names or free text.
   */
  specialty?: string[];
  /** Optional phone number */
  phone?: string;
}

/**
 * Response from POST /admin/onboard-user
 */
export interface OnboardUserResponse {
  success: boolean;
  userId?: string;
  profileId?: string;
  practitionerRoleId?: string;
  accessPolicyId?: string;
  userConfigurationId?: string;
  membershipId?: string;
  message?: string;
  error?: string;
}

/**
 * Response from GET /admin/roles
 */
export interface GetRolesResponse {
  success: boolean;
  roles: RoleOption[];
}

/**
 * Form values for the onboarding form (before mapping to API request).
 * `confirmPassword` is used only for client-side validation.
 */
export interface OnboardingFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  specialty: string;
  password: string;
  confirmPassword: string;
}

/**
 * Default role options used when the backend `/admin/roles` endpoint
 * is unavailable.
 */
export const DEFAULT_ROLE_OPTIONS: RoleOption[] = [
  {
    code: 'admin',
    display: 'Administrator',
    description: 'Full system access + user management',
    level: 5,
  },
  {
    code: 'practitioner',
    display: 'Practitioner',
    description: 'Medical professional, own patients',
    level: 3,
  },
  {
    code: 'frontdesk',
    display: 'Front Desk',
    description: 'Front desk, patient registration',
    level: 2,
  },
];
